import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface PartnerOrganization {
  id: string;
  name: string;
  logo_url: string;
  website_url?: string;
  sort_order: number;
}

const PartnerOrganizations = () => {
  const { t } = useTranslation('homepage');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, initialVisible: true });
  
  const { data: partners, isLoading } = useQuery({
    queryKey: ['partner-organizations'],
    queryFn: async () => {
      console.log('Fetching partner organizations...');
      const { data, error } = await supabase
        .from('partner_organizations')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      console.log('Partner organizations loaded:', data);
      return data as PartnerOrganization[];
    },
    staleTime: 30000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Show loading skeleton
  if (isLoading) {
    return (
      <section className="py-16 bg-warm-50" data-testid="partner-organizations">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-semibold text-warm-800 mb-4">
              {t('partnerOrganizations')}
            </h2>
            <p className="text-warm-500 max-w-2xl mx-auto">{t('partnerOrganizationsSubtitle')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-items-center">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-24 w-24 bg-muted rounded-lg" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // If no data after loading, don't render the section
  if (!partners || partners.length === 0) {
    console.log('No partner organizations to display');
    return null;
  }

  return (
    <section 
      ref={ref}
      className="py-16 bg-warm-50"
      data-testid="partner-organizations"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-display font-semibold text-warm-800 mb-4">
            {t('partnerOrganizations')}
          </h2>
          <p className="text-warm-500 max-w-2xl mx-auto">{t('partnerOrganizationsSubtitle')}</p>
        </div>
        
        <div className="flex flex-wrap gap-8 items-center justify-center max-w-2xl mx-auto">
          {partners.map((partner, index) => (
            <div 
              key={partner.id} 
              className={`flex items-center justify-center transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              {partner.website_url ? (
                <a
                  href={partner.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block transition-all duration-300 hover:scale-110"
                >
                  <img
                    src={partner.logo_url}
                    alt={partner.name}
                    className={`h-24 w-24 object-contain rounded-lg transition-all duration-300 ${
                      partner.logo_url.endsWith('.png') ? 'bg-black p-2' : ''
                    }`}
                  />
                </a>
              ) : (
                <img
                  src={partner.logo_url}
                  alt={partner.name}
                  className={`h-24 w-24 object-contain rounded-lg transition-all duration-300 ${
                    partner.logo_url.endsWith('.png') ? 'bg-black p-2' : ''
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnerOrganizations;
